import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Rss, 
  Plus, 
  RefreshCw, 
  ExternalLink, 
  Clock, 
  CheckCircle,
  Circle,
  Settings,
  Trash2,
  X,
  Loader
} from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Modal from '../../components/UI/Modal';
import './Feeds.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const FeedsList = () => {
  const [items, setItems] = useState([]);
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState({ unread_count: 0, total_items: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [selectedSource, setSelectedSource] = useState('all');
  const [newSource, setNewSource] = useState({ name: '', type: 'rss', url: '' });

  const fetchItems = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (selectedSource !== 'all') {
        params.append('source_id', selectedSource);
      }
      const res = await fetch(`${API_BASE}/api/feeds/items?${params}`);
      const data = await res.json();
      setItems(data);
    } catch (error) {
      console.error('Error fetching feed items:', error);
    }
  }, [selectedSource]);

  const fetchSources = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/feeds/sources`);
      const data = await res.json();
      setSources(data);
    } catch (error) {
      console.error('Error fetching sources:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/feeds/stats`);
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchItems(), fetchSources(), fetchStats()]);
    setLoading(false);
  }, [fetchItems]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      handleRefresh();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetch(`${API_BASE}/api/feeds/refresh`, { method: 'POST' });
      await loadData();
    } catch (error) {
      console.error('Error refreshing feeds:', error);
    }
    setRefreshing(false);
  };

  const handleAddSource = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE}/api/feeds/sources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSource)
      });
      if (res.ok) {
        setNewSource({ name: '', type: 'rss', url: '' });
        setShowAddModal(false);
        await fetchSources();
        // Immediately fetch from new source
        handleRefresh();
      }
    } catch (error) {
      console.error('Error adding source:', error);
    }
  };

  const handleDeleteSource = async (id) => {
    if (!window.confirm('确定要删除这个信息源吗？')) return;
    try {
      await fetch(`${API_BASE}/api/feeds/sources/${id}`, { method: 'DELETE' });
      await loadData();
    } catch (error) {
      console.error('Error deleting source:', error);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await fetch(`${API_BASE}/api/feeds/items/${id}/read`, { method: 'PATCH' });
      setItems(items.map(item => 
        item.id === id ? { ...item, read: true } : item
      ));
      setStats(prev => ({ ...prev, unread_count: Math.max(0, prev.unread_count - 1) }));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API_BASE}/api/feeds/items/mark-all-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedSource !== 'all' ? { source_id: parseInt(selectedSource) } : {})
      });
      setItems(items.map(item => ({ ...item, read: true })));
      await fetchStats();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return '刚刚';
    if (minutes < 60) return `${minutes}分钟前`;
    if (hours < 24) return `${hours}小时前`;
    if (days < 7) return `${days}天前`;
    return date.toLocaleDateString('zh-CN');
  };

  const getSourceIcon = (type) => {
    switch (type) {
      case 'rss': return <Rss size={14} />;
      default: return <Rss size={14} />;
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        <p>加载中...</p>
      </div>
    );
  }

  return (
    <div className="feeds-page">
      <div className="page-header">
        <div className="page-title">
          <h1>信息流</h1>
          <p className="page-subtitle">
            {stats.unread_count > 0 ? `${stats.unread_count} 条未读` : '全部已读'}
          </p>
        </div>
        <div className="header-actions">
          <Button 
            variant="secondary" 
            onClick={() => setShowSourcesModal(true)}
          >
            <Settings size={16} />
            <span>管理信息源</span>
          </Button>
          <Button 
            variant="secondary" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
            <span>{refreshing ? '刷新中...' : '刷新'}</span>
          </Button>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus size={16} />
            <span>添加信息源</span>
          </Button>
        </div>
      </div>

      {/* Source Filter */}
      <div className="source-filter">
        <button 
          className={`filter-btn ${selectedSource === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedSource('all')}
        >
          全部
        </button>
        {sources.map(source => (
          <button
            key={source.id}
            className={`filter-btn ${selectedSource === String(source.id) ? 'active' : ''}`}
            onClick={() => setSelectedSource(String(source.id))}
          >
            {getSourceIcon(source.type)}
            {source.name}
          </button>
        ))}
      </div>

      {/* Actions Bar */}
      {items.length > 0 && (
        <div className="actions-bar">
          <button className="text-btn" onClick={handleMarkAllRead}>
            <CheckCircle size={16} />
            全部标为已读
          </button>
        </div>
      )}

      {/* Feed Items List */}
      {items.length === 0 ? (
        <Card className="empty-state">
          <Rss size={48} />
          <h3>暂无信息</h3>
          <p>添加信息源后，新内容将显示在这里</p>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus size={18} />
            添加第一个信息源
          </Button>
        </Card>
      ) : (
        <div className="feed-items-list">
          {items.map(item => (
            <Card 
              key={item.id} 
              className={`feed-item ${item.read ? 'read' : 'unread'}`}
            >
              <div className="feed-item-header">
                <div className="feed-source-badge">
                  {getSourceIcon(item.source?.type)}
                  <span>{item.source?.name || '未知来源'}</span>
                </div>
                <div className="feed-item-time">
                  <Clock size={12} />
                  {formatTime(item.published_at)}
                </div>
              </div>
              
              <Link 
                to={`/feeds/${item.id}`} 
                className="feed-item-content"
                onClick={() => !item.read && handleMarkAsRead(item.id)}
              >
                <h3 className="feed-item-title">
                  {!item.read && <Circle size={8} className="unread-dot" />}
                  {item.title}
                </h3>
                {item.summary && (
                  <p className="feed-item-summary">{item.summary}</p>
                )}
              </Link>

              <div className="feed-item-footer">
                <span className="feed-item-author">{item.author}</span>
                <a 
                  href={item.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="external-link"
                >
                  <ExternalLink size={14} />
                  原文
                </a>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add Source Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="添加信息源"
      >
        <form onSubmit={handleAddSource}>
          <div className="form-group">
            <label>名称</label>
            <input
              type="text"
              value={newSource.name}
              onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
              placeholder="例如：TechCrunch"
              required
            />
          </div>
          <div className="form-group">
            <label>类型</label>
            <select
              value={newSource.type}
              onChange={(e) => setNewSource({ ...newSource, type: e.target.value })}
            >
              <option value="rss">RSS Feed</option>
              <option value="x" disabled>X / Twitter (即将支持)</option>
              <option value="google" disabled>Google Alerts (即将支持)</option>
              <option value="outlook" disabled>Outlook 邮箱 (即将支持)</option>
            </select>
          </div>
          <div className="form-group">
            <label>RSS URL</label>
            <input
              type="url"
              value={newSource.url}
              onChange={(e) => setNewSource({ ...newSource, url: e.target.value })}
              placeholder="https://example.com/feed.xml"
              required
            />
          </div>
          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={() => setShowAddModal(false)}>
              取消
            </Button>
            <Button type="submit">
              添加
            </Button>
          </div>
        </form>
      </Modal>

      {/* Manage Sources Modal */}
      <Modal
        isOpen={showSourcesModal}
        onClose={() => setShowSourcesModal(false)}
        title="管理信息源"
      >
        {sources.length === 0 ? (
          <div className="empty-sources">
            <p>暂无信息源</p>
          </div>
        ) : (
          <div className="sources-list">
            {sources.map(source => (
              <div key={source.id} className="source-item">
                <div className="source-info">
                  <div className="source-icon">{getSourceIcon(source.type)}</div>
                  <div className="source-details">
                    <span className="source-name">{source.name}</span>
                    <span className="source-url">{source.url}</span>
                  </div>
                </div>
                <button 
                  className="delete-btn"
                  onClick={() => handleDeleteSource(source.id)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FeedsList;
